import { Link, useLocation, useParams } from "react-router-dom";
import HeaderNavBar from "../../../components/header-nav-bar";
import { useCallback, useEffect, useState } from "react";
import { ActiveWorkerDto } from "../../../types/work.types";
import { format } from "date-fns";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmModal from "../../../components/confirm-modal";
import { handleError } from "../../../common/utils";

interface ActiveWorkersProps {
  title: string;
  emptyMessage: string;
  getWorkersFn: (matterId: number) => Promise<ActiveWorkerDto[]>;
  unassignWorkerFn: (matterId: number, userId: number) => Promise<any>;
  unassignAllWorkersFn: (matterId: number) => Promise<any>;
  supportManagerContext?: boolean;
}

export default function ActiveWorkers({
  title,
  emptyMessage,
  getWorkersFn,
  unassignWorkerFn,
  unassignAllWorkersFn,
  supportManagerContext = true,
}: ActiveWorkersProps) {
  const params = useParams();
  const location = useLocation();
  const matterId = Number(params?.matterId || "");

  const [isManagerContext, setIsManagerContext] = useState(false);
  const [workersByRole, setWorkersByRole] = useState<Map<string, ActiveWorkerDto[]>>(new Map());
  const [showUnassignUserModal, setShowUnassignUserModal] = useState(false);
  const [showUnassignAllModal, setShowUnassignAllModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<ActiveWorkerDto | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const loadActiveWorkers = useCallback(
    (matterId: number) => {
      getWorkersFn(matterId).then((workers) => {
        const sortedWorkers = workers.sort((a, b) => b.role.localeCompare(a.role));
        const dictionary = new Map();
        sortedWorkers.forEach((worker) => {
          if (dictionary.has(worker.role)) {
            dictionary.get(worker.role).push(worker);
          } else {
            dictionary.set(worker.role, [worker]);
          }
        });
        setWorkersByRole(dictionary);
      }).catch((err) => {
        handleError(err, setError);
      });
    },
    [getWorkersFn]
  );

  useEffect(() => {
    loadActiveWorkers(matterId);
  }, [loadActiveWorkers, matterId]);

  useEffect(() => {
    if (supportManagerContext) {
      if (location.pathname.includes("/manager/")) {
        setIsManagerContext(true);
      } else {
        setIsManagerContext(false);
      }
    }
  }, [location.pathname, supportManagerContext]);

  const handleUnassignUser = (worker: ActiveWorkerDto) => {
    setSelectedWorker(worker);
    setShowUnassignUserModal(true);
  };

  const confirmUnassignUser = async () => {
    if (!selectedWorker) return;
    setIsLoading(true);
    setError("");
    try {
      await unassignWorkerFn(matterId, selectedWorker.userId);
      loadActiveWorkers(matterId);
    } catch (err) {
      handleError(err, setError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassignAll = () => {
    setShowUnassignAllModal(true);
  };

  const confirmUnassignAll = async () => {
    setIsLoading(true);
    setError("");
    try {
      await unassignAllWorkersFn(matterId);
      loadActiveWorkers(matterId);
    } catch (err) {
      handleError(err, setError);
    } finally {
      setIsLoading(false);
    }
  };

  const hasWorkers = workersByRole && workersByRole.size > 0;

  return (
    <div className="container">
      {!isManagerContext && <HeaderNavBar backTo="details" shouldHideMatter={false} />}
      {isManagerContext && (
        <div className="d-flex flex-row mb-4">
          <div className="me-auto" style={{ marginLeft: "-12px" }}>
            <Link to={`/manager/matters/${matterId}/details`} className="btn btn-sw-m btn-sm">
              <FontAwesomeIcon icon={faCircleLeft} className="me-2" />
              Back to matter
            </Link>
          </div>
        </div>
      )}
      <div className="row tablet-view justify-content-start">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-2">
            <div className="mb-auto">
              <span>Administrator</span>
              <h2>{title}</h2>
            </div>
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            {error && <div className="alert alert-danger">{error}</div>}
            {!hasWorkers && <p> {emptyMessage}</p>}
            {hasWorkers && (
              <div>
                {Array.from(workersByRole.entries()).map(([role, worker]) => (
                  <div key={role} className="mt-3" style={{ maxHeight: 400, overflowY: "auto", overflowX: "hidden" }}>
                    <h3>{role}</h3>
                    <table className="sw">
                      <thead>
                        <tr className="d-flex">
                          <th scope="col" className="col-sm-4">
                            Name
                          </th>
                          <th scope="col" className="col-sm-3">
                            Last Active
                          </th>
                          <th scope="col" className="col-sm-2">
                            Items
                          </th>
                          <th scope="col" className="col-sm-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {worker.map((w) => (
                          <tr key={w.userId} className="d-flex border-bottom" title={`IDs: ${w.workIds?.join(", ")}`}>
                            <td className="col-sm-4">{w.fullName}</td>
                            <td className="col-sm-3">{format(w.lastActive + "Z", "PPP p")}</td>
                            <td className="col-sm-2">{w.workItemCount}</td>
                            <td className="col-sm-3">
                              <button
                                className="btn btn-sw-m btn-sm"
                                onClick={() => handleUnassignUser(w)}
                                disabled={isLoading}
                              >
                                Unassign
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                <button
                  className="btn btn-sw-m btn-sm mt-3"
                  onClick={handleUnassignAll}
                  disabled={isLoading}
                >
                  Unassign All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showUnassignUserModal}
        title="Unassign Worker"
        message={
          selectedWorker ? (
            <div>
              <p>Are you sure you want to unassign all {selectedWorker.workItemCount} work item(s) from <strong>{selectedWorker.fullName}</strong>?</p>
              <p className="mb-1">Work IDs that will be unassigned:</p>
              <ul style={{ maxHeight: 200, overflowY: "auto" }}>
                {selectedWorker.workIds.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            </div>
          ) : null
        }
        action={confirmUnassignUser}
        close={() => setShowUnassignUserModal(false)}
      />

      <ConfirmModal
        isOpen={showUnassignAllModal}
        title="Unassign All Workers"
        message="Are you sure you want to unassign all work from all active workers?"
        action={confirmUnassignAll}
        close={() => setShowUnassignAllModal(false)}
      />
    </div>
  );
}
